<template>
  <div class="mi-cuenta" :class="{ dark: isDark }">

    <!-- ── Encabezado ── -->
    <div class="mc-header">
      <div class="mc-header-left">
        <h1 class="mc-title">
          <i class="bi bi-credit-card-2-front"></i>
          Plan & Facturación
        </h1>
        <p class="mc-sub">Gestiona tu suscripción y comprobantes de pago</p>
      </div>
    </div>

    <!-- ── Cargando ── -->
    <div v-if="cargando" class="mc-loading">
      <div class="spinner"></div>
      <span>Cargando información...</span>
    </div>

    <template v-else>

      <!-- ── Tarjetas de estado ── -->
      <div class="stats-grid">

        <div class="stat-card plan stat-card-clickable" @click="abrirModalPlanes" title="Clic para ver planes disponibles">
          <div class="stat-icon"><i class="bi bi-grid-3x3-gap-fill"></i></div>
          <div class="stat-body">
            <span class="stat-label">Plan actual</span>
            <span class="stat-val">{{ nombrePlan }}</span>
            <span class="stat-date" style="color:#c09d62;font-weight:600">
              <i class="bi bi-arrow-left-right" style="font-size:.7rem"></i> Cambiar plan
            </span>
          </div>
        </div>

        <div class="stat-card" :class="estadoClass">
          <div class="stat-icon"><i :class="estadoIcono"></i></div>
          <div class="stat-body">
            <span class="stat-label">Estado</span>
            <span class="stat-val">{{ estadoTexto }}</span>
          </div>
        </div>

        <div class="stat-card vencimiento">
          <div class="stat-icon"><i class="bi bi-calendar3"></i></div>
          <div class="stat-body">
            <span class="stat-label">
              {{ info.suscripcion?.dias_restantes >= 0 ? 'Vence en' : 'Venció hace' }}
            </span>
            <span class="stat-val">
              <template v-if="info.suscripcion?.dias_restantes != null">
                {{ Math.abs(info.suscripcion.dias_restantes) }} día{{ Math.abs(info.suscripcion.dias_restantes) !== 1 ? 's' : '' }}
              </template>
              <template v-else>—</template>
            </span>
            <span class="stat-date" v-if="info.empresa?.fecha_vencimiento">
              {{ formatFecha(info.empresa.fecha_vencimiento) }}
            </span>
          </div>
        </div>

        <div class="stat-card ultimo-pago">
          <div class="stat-icon"><i class="bi bi-receipt"></i></div>
          <div class="stat-body">
            <!-- Hay comprobante subido por el CEO -->
            <template v-if="info.ultimo_pago">
              <span class="stat-label">Último comprobante</span>
              <span class="stat-val">
                L {{ Number(info.ultimo_pago.monto).toLocaleString('es-HN', { minimumFractionDigits: 2 }) }}
              </span>
              <span class="stat-date">
                {{ meses[info.ultimo_pago.periodo_mes - 1] }} {{ info.ultimo_pago.periodo_anio }}
                <span class="pago-badge" :class="info.ultimo_pago.estado">
                  {{ estadoPago(info.ultimo_pago.estado) }}
                </span>
              </span>
            </template>
            <!-- No hay comprobante pero sí hay renovación de Perdomo -->
            <template v-else-if="info.ultima_renovacion">
              <span class="stat-label">Última renovación</span>
              <span class="stat-val">+{{ info.ultima_renovacion.meses }} mes{{ info.ultima_renovacion.meses !== 1 ? 'es' : '' }}</span>
              <span class="stat-date">
                Vence: {{ formatFecha(info.ultima_renovacion.nueva_fecha_vencimiento) }}
              </span>
            </template>
            <!-- Sin ningún registro -->
            <template v-else>
              <span class="stat-label">Último pago</span>
              <span class="stat-val dim">Sin registros</span>
            </template>
          </div>
        </div>

      </div>

      <!-- ── Acceso rápido proforma — solo el día de vencimiento y sin comprobante enviado ── -->
      <div class="mc-proforma-hint" v-if="mostrarProforma">
        <i class="bi bi-file-earmark-text"></i>
        <span>Tu servicio ha vencido — genera tu proforma para renovar antes del bloqueo.</span>
        <router-link :to="'/proforma?id_plan=' + idPlanActual" class="btn-proforma">
          Ver Proforma <i class="bi bi-arrow-right"></i>
        </router-link>
      </div>

      <!-- ── Solicitud activa de cambio de plan ── -->
      <div class="mc-solicitud-banner" v-if="solicitudActiva">
        <div class="solicitud-banner-ico"><i class="bi bi-arrow-left-right"></i></div>
        <div class="solicitud-banner-body">
          <span class="solicitud-banner-titulo">Cambio de plan solicitado</span>
          <span class="solicitud-banner-desc">
            Solicitaste cambiar al plan <strong>{{ solicitudActiva.nombre_plan_nuevo }}</strong>.
            Tienes hasta el <strong>{{ formatFecha(solicitudActiva.fecha_limite) }}</strong> para
            enviar el comprobante de pago. Si no se recibe, la solicitud expirará automáticamente.
          </span>
        </div>
        <button class="btn-cancelar-solicitud" :disabled="cancelandoSolicitud" @click="cancelarSolicitud">
          <i class="bi bi-x-circle"></i> {{ cancelandoSolicitud ? 'Cancelando…' : 'Cancelar solicitud' }}
        </button>
      </div>

      <!-- ── Subir comprobante ── -->
      <div class="mc-section">
        <h2 class="section-title">
          <i class="bi bi-upload"></i>
          Subir comprobante de pago
        </h2>

        <div class="upload-card">
          <div class="upload-form">

            <div class="form-row">
              <div class="field">
                <label>Período — Mes</label>
                <select v-model="form.periodo_mes">
                  <option v-for="(m, i) in meses" :key="i" :value="i + 1">{{ m }}</option>
                </select>
              </div>
              <div class="field">
                <label>Año</label>
                <input type="number" v-model.number="form.periodo_anio" min="2024" max="2099" />
              </div>
              <div class="field">
                <label>Monto pagado (L)</label>
                <input type="number" v-model.number="form.monto" step="0.01" min="0" placeholder="0.00" />
              </div>
            </div>

            <!-- Zona drag & drop -->
            <div
              class="drop-zone"
              :class="{ active: dragging, 'has-file': archivoSeleccionado }"
              @dragover.prevent="dragging = true"
              @dragleave.prevent="dragging = false"
              @drop.prevent="onDrop"
              @click="$refs.fileInput.click()"
            >
              <input
                ref="fileInput"
                type="file"
                accept=".jpg,.jpeg,.png,.pdf,.webp"
                style="display:none"
                @change="onFileChange"
              />
              <template v-if="!archivoSeleccionado">
                <i class="bi bi-cloud-arrow-up drop-icon"></i>
                <p class="drop-text">Arrastra el comprobante aquí o <u>haz clic para seleccionar</u></p>
                <p class="drop-hint">JPG, PNG, WEBP o PDF · máx. 10 MB</p>
              </template>
              <template v-else>
                <i class="bi bi-file-earmark-check drop-icon selected"></i>
                <p class="drop-text">{{ archivoSeleccionado.name }}</p>
                <p class="drop-hint">{{ formatSize(archivoSeleccionado.size) }} · clic para cambiar</p>
              </template>
            </div>

            <button
              class="btn-subir"
              :disabled="subiendo || !archivoSeleccionado"
              @click="subirComprobante"
            >
              <span v-if="!subiendo"><i class="bi bi-send-fill"></i> Enviar comprobante</span>
              <span v-else><i class="bi bi-hourglass-split"></i> Enviando...</span>
            </button>

            <div v-if="mensajeUpload" class="upload-msg" :class="uploadOk ? 'ok' : 'error'">
              <i :class="uploadOk ? 'bi bi-check-circle-fill' : 'bi bi-x-circle-fill'"></i>
              {{ mensajeUpload }}
            </div>

          </div>
        </div>
      </div>

      <!-- ── Historial de pagos ── -->
      <div class="mc-section">
        <h2 class="section-title">
          <i class="bi bi-clock-history"></i>
          Historial de pagos
        </h2>

        <div v-if="historialCargando" class="mc-loading small">
          <div class="spinner small"></div><span>Cargando...</span>
        </div>

        <div v-else-if="historial.length === 0" class="empty-state">
          <i class="bi bi-inbox"></i>
          <p>Aún no hay pagos registrados</p>
        </div>

        <div v-else class="tabla-wrap">
          <table class="historial-table">
            <thead>
              <tr>
                <th>Tipo</th>
                <th>Detalle</th>
                <th>Comprobante</th>
                <th>Estado</th>
                <th>Fecha</th>
                <th>Notas</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(entrada, i) in historial" :key="i">
                <!-- Tipo -->
                <td>
                  <span v-if="entrada._tipo === 'renovacion'" class="tipo-badge tipo-renovacion">
                    <i class="bi bi-arrow-repeat"></i> Renovación
                  </span>
                  <span v-else class="tipo-badge tipo-comprobante">
                    <i class="bi bi-upload"></i> Comprobante
                  </span>
                </td>

                <!-- Detalle -->
                <td v-if="entrada._tipo === 'renovacion'" class="td-periodo">
                  +{{ entrada.meses }} mes{{ entrada.meses !== 1 ? 'es' : '' }}
                  <span class="td-sub">→ {{ formatFecha(entrada.nueva_fecha_vencimiento) }}</span>
                </td>
                <td v-else class="td-periodo">
                  {{ meses[entrada.periodo_mes - 1] }} {{ entrada.periodo_anio }}
                  <span class="td-sub td-monto-inline">L {{ Number(entrada.monto).toLocaleString('es-HN', { minimumFractionDigits: 2 }) }}</span>
                </td>

                <!-- Comprobante -->
                <td class="td-file">
                  <a
                    v-if="entrada.ruta_comprobante"
                    :href="apiUrl + '/cuenta/comprobante/' + encodeURIComponent(entrada.ruta_comprobante.replace('pagos/', ''))"
                    target="_blank"
                    class="link-comprobante"
                    :title="entrada.nombre_archivo"
                  >
                    <i class="bi bi-file-earmark-arrow-down"></i> Ver
                  </a>
                  <span v-else class="dim">—</span>
                </td>

                <!-- Estado -->
                <td>
                  <span v-if="entrada._tipo === 'renovacion'" class="pago-badge aprobado">Aplicada</span>
                  <span v-else class="pago-badge" :class="entrada.estado">{{ estadoPago(entrada.estado) }}</span>
                </td>

                <!-- Fecha -->
                <td class="td-fecha">{{ formatFechaCorta(entrada._fecha) }}</td>

                <!-- Notas -->
                <td class="td-nota">
                  <span v-if="entrada.notas || entrada.notas_perdomo" class="nota-text"
                    :title="entrada.notas || entrada.notas_perdomo">
                    <i class="bi bi-chat-left-text-fill"></i>
                    {{ (entrada.notas || entrada.notas_perdomo).slice(0, 40) }}{{ (entrada.notas || entrada.notas_perdomo).length > 40 ? '…' : '' }}
                  </span>
                  <span v-else class="dim">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ── Descargar datos ── -->
      <div class="mc-section export-zone">
        <h2 class="section-title export">
          <i class="bi bi-cloud-download-fill"></i>
          Exportar mis datos
        </h2>
        <p class="danger-desc" style="color: inherit; opacity: .8;">
          Descargá toda la información de tu empresa y sucursales en archivos Excel
          (ventas, compras, clientes, productos, inventario, cotizaciones, nómina y más).
        </p>
        <button class="btn-exportar" @click="descargarDatos" :disabled="descargando">
          <i class="bi bi-file-earmark-zip-fill"></i>
          {{ descargando ? 'Generando ZIP…' : 'Descargar ZIP completo' }}
        </button>
        <p v-if="mensajeDescarga" class="cancel-msg" :class="{ ok: descargaOk }">{{ mensajeDescarga }}</p>
      </div>

      <!-- ── Cancelar suscripción ── -->
      <div class="mc-section danger-zone" v-if="info.suscripcion?.puede_cancelar">
        <h2 class="section-title danger">
          <i class="bi bi-exclamation-triangle-fill"></i>
          Cancelar suscripción
        </h2>
        <p class="danger-desc">
          Seguirás teniendo acceso hasta el <strong>vencimiento de tu período actual</strong>
          ({{ formatFecha(info.empresa?.fecha_vencimiento) }}).
          Después el sistema quedará suspendido. Esta acción no genera reembolsos.
        </p>
        <button class="btn-cancelar" @click="confirmarCancelacion" :disabled="cancelando">
          <i class="bi bi-slash-circle"></i>
          {{ cancelando ? 'Procesando…' : 'Cancelar suscripción' }}
        </button>
        <p v-if="mensajeCancelacion" class="cancel-msg">{{ mensajeCancelacion }}</p>
      </div>

    </template>

    <!-- ── Modal confirmación cancelación suscripción ── -->
    <transition name="fade">
      <div class="modal-overlay" v-if="modalCancelacion">
        <div class="modal-card">
          <div class="modal-icon"><i class="bi bi-exclamation-octagon-fill"></i></div>
          <h3>¿Cancelar tu suscripción?</h3>
          <p>
            Mantendrás acceso hasta el <strong>{{ formatFecha(info.empresa?.fecha_vencimiento) }}</strong>.
            Después el sistema quedará suspendido. Esta acción <strong>no genera reembolsos</strong>.
          </p>
          <div class="modal-btns">
            <button class="btn-modal-cancel" @click="modalCancelacion = false">No, mantener</button>
            <button class="btn-modal-confirm" @click="ejecutarCancelacion">Sí, cancelar</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ── Modal selector de planes ── -->
    <transition name="fade">
      <div class="modal-overlay" v-if="modalPlanes">
        <div class="modal-card modal-planes-card">
          <div class="modal-planes-header">
            <div>
              <h3><i class="bi bi-layers-fill" style="color:#c09d62"></i> Planes disponibles</h3>
              <p>Selecciona el plan al que deseas cambiar</p>
            </div>
            <button class="modal-x" @click="modalPlanes = false"><i class="bi bi-x-lg"></i></button>
          </div>

          <!-- Solicitud activa -->
          <div class="planes-solicitud-activa" v-if="solicitudActiva">
            <i class="bi bi-hourglass-split"></i>
            <span>
              Tienes una solicitud pendiente para cambiar al plan
              <strong>{{ solicitudActiva.nombre_plan_nuevo }}</strong>.
              Fecha límite: <strong>{{ formatFecha(solicitudActiva.fecha_limite) }}</strong>.
            </span>
            <button class="btn-cancel-sol" :disabled="cancelandoSolicitud" @click="cancelarSolicitud">
              {{ cancelandoSolicitud ? '…' : 'Cancelar' }}
            </button>
          </div>

          <!-- Loading -->
          <div v-if="planesLoading" class="planes-loading">
            <div class="spinner small"></div><span>Cargando planes…</span>
          </div>

          <!-- Grid de planes -->
          <div v-else class="planes-modal-grid">
            <div
              v-for="plan in planes"
              :key="plan.id"
              class="plan-modal-card"
              :class="{
                'pmc-actual':     plan.id === idPlanActual,
                'pmc-solicitado': solicitudActiva && plan.id === solicitudActiva.id_plan_nuevo,
              }"
            >
              <!-- Badge estado -->
              <span class="pmc-tag pmc-tag-actual" v-if="plan.id === idPlanActual">
                <i class="bi bi-check-circle-fill"></i> Actual
              </span>
              <span class="pmc-tag pmc-tag-sol" v-else-if="solicitudActiva && plan.id === solicitudActiva.id_plan_nuevo">
                <i class="bi bi-hourglass-split"></i> Pendiente
              </span>

              <div class="pmc-nombre">{{ plan.nombre }}</div>
              <div class="pmc-desc" v-if="plan.descripcion">{{ plan.descripcion }}</div>

              <div class="pmc-precio" v-if="plan.precio">
                <span class="pmc-usd">${{ Number(plan.precio).toFixed(2) }}</span>
                <span class="pmc-per">/ {{ plan.periodo_tipo || 'mensual' }}</span>
              </div>
              <div class="pmc-precio pmc-gratis" v-else>
                <i class="bi bi-gift-fill"></i> Gratuito
              </div>

              <button
                v-if="plan.id !== idPlanActual && !solicitudActiva"
                class="btn-sel-plan"
                @click="confirmarCambioPlan(plan)"
              >
                Seleccionar <i class="bi bi-arrow-right"></i>
              </button>
            </div>
          </div>

          <p class="planes-nota-modal">
            <i class="bi bi-info-circle"></i>
            <span>Al solicitar un cambio tendrás <strong>5 días</strong> para enviar el comprobante. El cambio se aplica cuando Perdomo &amp; Asociados apruebe el pago.</span>
          </p>
        </div>
      </div>
    </transition>

    <!-- ── Modal confirmación cambio de plan ── -->
    <transition name="fade">
      <div class="modal-overlay" v-if="modalCambioPlan">
        <div class="modal-card">
          <div class="modal-icon cambio"><i class="bi bi-arrow-left-right"></i></div>
          <h3>¿Solicitar cambio de plan?</h3>
          <p>
            Pasarás del plan <strong>{{ nombrePlan }}</strong> al plan
            <strong>{{ planSolicitado?.nombre }}</strong>.<br>
            Tendrás <strong>5 días</strong> para enviar el comprobante de pago.
            Si no lo envías, la solicitud expirará y se mantendrá tu plan actual.
          </p>
          <div class="modal-btns">
            <button class="btn-modal-cancel" @click="modalCambioPlan = false">Cancelar</button>
            <button class="btn-modal-cambio" :disabled="solicitandoCambio" @click="ejecutarCambioPlan">
              <i class="bi" :class="solicitandoCambio ? 'bi-hourglass-split' : 'bi-check-circle-fill'"></i>
              {{ solicitandoCambio ? 'Enviando…' : 'Sí, solicitar' }}
            </button>
          </div>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import { setPageTitle } from "@/components/pageMetadata";
const { getApi } = require("../../config/getApiUrl.js");

export default {
  name: "MiCuenta",
  data() {
    const ahora = new Date();
    return {
      isDark:     localStorage.getItem("isDarkMode") === "true",
      cargando:   true,
      historialCargando: false,
      info:       {},
      historial:  [],
      form: {
        periodo_mes:  ahora.getMonth() + 1,
        periodo_anio: ahora.getFullYear(),
        monto:        null,
      },
      archivoSeleccionado: null,
      dragging:    false,
      subiendo:    false,
      mensajeUpload: null,
      uploadOk:    false,
      cancelando:  false,
      modalCancelacion: false,
      mensajeCancelacion: null,
      descargando: false,
      mensajeDescarga: null,
      descargaOk: false,
      // Cambio de plan
      planes:            [],
      planesLoading:     false,
      solicitudActiva:   null,
      planSolicitado:    null,
      modalPlanes:       false,
      modalCambioPlan:   false,
      solicitandoCambio: false,
      cancelandoSolicitud: false,
      meses: ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'],
      apiUrl: getApi(),
    };
  },
  computed: {
    nombrePlan() {
      const pc = this.info.empresa?.plan_config;
      return pc?.nombre || 'Plan activo';
    },
    idPlanActual() {
      return this.info.empresa?.plan_config?.id ?? this.info.empresa?.plan_config?.id_plan ?? null;
    },

    // Mostrar enlace proforma si:
    //   1. Hay un plan asignado
    //   2. La suscripción ya venció (incluye cuando está bloqueada)
    //   3. No hay comprobante pendiente ni aprobado para el mes actual
    mostrarProforma() {
      if (!this.idPlanActual) return false;

      const fv = this.info.empresa?.fecha_vencimiento;
      if (!fv) return false;

      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);
      // Parsear solo YYYY-MM-DD para evitar desfase UTC
      const [y, m, d] = String(fv).substring(0, 10).split('-').map(Number);
      const fechaVenc = new Date(y, m - 1, d);
      // Mostrar desde el día de vencimiento en adelante (incluso si ya está bloqueado)
      if (hoy < fechaVenc) return false;

      // Ocultar si ya mandaron comprobante pendiente o aprobado
      const up = this.info.ultimo_pago;
      if (up && (up.estado === 'pendiente' || up.estado === 'aprobado')) {
        return false;
      }

      return true;
    },
    estadoClass() {
      const e = this.info.suscripcion?.estado;
      return { vigente: e === 'vigente', por_vencer: e === 'por_vencer', vencida: e === 'vencida' || e === 'suspendida' };
    },
    estadoTexto() {
      const map = { vigente: 'Vigente', por_vencer: 'Por vencer', vencida: 'Vencida', suspendida: 'Suspendida', sin_info: 'Sin info' };
      return map[this.info.suscripcion?.estado] || '—';
    },
    estadoIcono() {
      const e = this.info.suscripcion?.estado;
      if (e === 'vigente')     return 'bi bi-check-circle-fill';
      if (e === 'por_vencer')  return 'bi bi-exclamation-circle-fill';
      return 'bi bi-x-circle-fill';
    },
  },
  async mounted() {
    setPageTitle("Plan & Facturación — POSWAVE");
    window.addEventListener("poswave:toggle-dark", this.syncDark);
    await this.cargarInfo();
    await this.cargarHistorial();
    await Promise.all([this.cargarPlanes(), this.cargarSolicitud()]);
  },
  beforeUnmount() {
    window.removeEventListener("poswave:toggle-dark", this.syncDark);
  },
  methods: {
    syncDark() { this.isDark = localStorage.getItem("isDarkMode") === "true"; },
    token() { return localStorage.getItem("auth"); },

    async cargarInfo() {
      this.cargando = true;
      try {
        const r = await fetch(`${this.apiUrl}/cuenta/info`, {
          headers: { Authorization: `Bearer ${this.token()}` },
        });
        if (r.ok) {
          this.info = await r.json();
          // Sincronizar flag comprobante_pendiente con el estado real del último pago
          const up = this.info.ultimo_pago;
          if (up && up.estado === 'pendiente') {
            localStorage.setItem('comprobante_pendiente', '1');
          } else {
            // Aprobado (ya no se necesita el flag) o rechazado (vuelve a bloquear)
            localStorage.removeItem('comprobante_pendiente');
          }
        }
        else { this.info = {}; }
      } finally { this.cargando = false; }
    },

    async cargarHistorial() {
      this.historialCargando = true;
      try {
        const r = await fetch(`${this.apiUrl}/cuenta/pagos`, {
          headers: { Authorization: `Bearer ${this.token()}` },
        });
        this.historial = r.ok ? await r.json() : [];
      } finally { this.historialCargando = false; }
    },

    onFileChange(e) {
      this.archivoSeleccionado = e.target.files[0] || null;
      this.mensajeUpload = null;
    },
    onDrop(e) {
      this.dragging = false;
      const file = e.dataTransfer.files[0];
      if (file) { this.archivoSeleccionado = file; this.mensajeUpload = null; }
    },

    async subirComprobante() {
      if (!this.archivoSeleccionado || !this.form.monto) {
        this.mensajeUpload = 'Completa el monto y adjunta el comprobante.';
        this.uploadOk = false;
        return;
      }
      this.subiendo = true;
      this.mensajeUpload = null;
      try {
        const fd = new FormData();
        fd.append('comprobante',  this.archivoSeleccionado);
        fd.append('monto',        this.form.monto);
        fd.append('periodo_mes',  this.form.periodo_mes);
        fd.append('periodo_anio', this.form.periodo_anio);

        const r = await fetch(`${this.apiUrl}/cuenta/pago`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${this.token()}` },
          body: fd,
        });
        const data = await r.json();
        if (r.ok) {
          this.uploadOk = true;
          this.mensajeUpload = data.message;
          this.archivoSeleccionado = null;
          this.form.monto = null;
          if (this.$refs.fileInput) this.$refs.fileInput.value = '';
          // Comprobante pendiente desbloquea el sistema mientras Perdomo revisa
          localStorage.setItem('comprobante_pendiente', '1');
          await this.cargarInfo();
          await this.cargarHistorial();
        } else {
          this.uploadOk = false;
          this.mensajeUpload = data.message || 'Error al subir el comprobante.';
        }
      } catch {
        this.uploadOk = false;
        this.mensajeUpload = 'Error de red. Intenta de nuevo.';
      } finally {
        this.subiendo = false;
      }
    },

    abrirModalPlanes() { this.modalPlanes = true; },

    async cargarPlanes() {
      this.planesLoading = true;
      try {
        const r = await fetch(`${this.apiUrl}/cuenta/planes`, {
          headers: { Authorization: `Bearer ${this.token()}` },
        });
        this.planes = r.ok ? await r.json() : [];
      } finally { this.planesLoading = false; }
    },

    async cargarSolicitud() {
      try {
        const r = await fetch(`${this.apiUrl}/cuenta/cambio-plan`, {
          headers: { Authorization: `Bearer ${this.token()}` },
        });
        this.solicitudActiva = r.ok ? await r.json() : null;
      } catch { this.solicitudActiva = null; }
    },

    confirmarCambioPlan(plan) {
      this.planSolicitado  = plan;
      this.modalPlanes     = false;
      this.modalCambioPlan = true;
    },

    async ejecutarCambioPlan() {
      if (!this.planSolicitado) return;
      this.solicitandoCambio = true;
      try {
        const r = await fetch(`${this.apiUrl}/cuenta/cambio-plan`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${this.token()}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ id_plan_nuevo: this.planSolicitado.id }),
        });
        const data = await r.json();
        if (r.ok) {
          this.solicitudActiva = data;
          this.modalCambioPlan = false;
        } else {
          alert(data.error || 'No se pudo crear la solicitud.');
        }
      } catch {
        alert('Error de red. Intenta de nuevo.');
      } finally {
        this.solicitandoCambio = false;
      }
    },

    async cancelarSolicitud() {
      if (!this.solicitudActiva) return;
      this.cancelandoSolicitud = true;
      try {
        const r = await fetch(`${this.apiUrl}/cuenta/cambio-plan/${this.solicitudActiva.id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${this.token()}` },
        });
        if (r.ok) {
          this.solicitudActiva = null;
          // Refrescar también el banner si existiera
        } else {
          const data = await r.json();
          alert(data.error || 'No se pudo cancelar la solicitud.');
        }
      } catch {
        alert('Error de red. Intenta de nuevo.');
      } finally {
        this.cancelandoSolicitud = false;
      }
    },

    async descargarDatos() {
      this.descargando = true;
      this.mensajeDescarga = null;
      this.descargaOk = false;
      try {
        const r = await fetch(`${this.apiUrl}/empresa/exportar-datos`, {
          headers: { Authorization: `Bearer ${this.token()}` },
        });
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          this.mensajeDescarga = err.error || 'Error al generar el archivo.';
          return;
        }
        const blob = await r.blob();
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        const fecha = new Date().toISOString().substring(0, 10);
        a.href     = url;
        a.download = `POSWAVE_datos_${fecha}.zip`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        this.descargaOk = true;
        this.mensajeDescarga = 'Descarga iniciada correctamente.';
      } catch {
        this.mensajeDescarga = 'Error de conexión al descargar.';
      } finally {
        this.descargando = false;
      }
    },

    confirmarCancelacion() { this.modalCancelacion = true; },

    async ejecutarCancelacion() {
      this.modalCancelacion = false;
      this.cancelando = true;
      try {
        const r = await fetch(`${this.apiUrl}/cuenta/suscripcion`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${this.token()}` },
        });
        const data = await r.json();
        this.mensajeCancelacion = data.message;
        if (r.ok) {
          // Actualizar estado en localStorage para que el router guard sepa que está cancelada
          // (evita loop /plan-vencido ↔ /home al expirar la fecha)
          localStorage.setItem('estado_suscripcion', 'cancelada');
          if (data.fecha_acceso_hasta) {
            localStorage.setItem('fecha_vencimiento', data.fecha_acceso_hasta);
          }
          await this.cargarInfo();
        }
      } catch {
        this.mensajeCancelacion = 'Error al procesar la cancelación.';
      } finally {
        this.cancelando = false;
      }
    },

    estadoPago(estado) {
      return { pendiente: 'En revisión', aprobado: 'Aprobado', rechazado: 'Rechazado' }[estado] || estado;
    },
    formatFecha(d) {
      if (!d) return '—';
      // Parsear YYYY-MM-DD directamente para evitar desfase UTC
      const [y, m, day] = String(d).substring(0, 10).split('-').map(Number);
      const meses = ['enero','febrero','marzo','abril','mayo','junio',
                     'julio','agosto','septiembre','octubre','noviembre','diciembre'];
      return `${day} de ${meses[m - 1]} de ${y}`;
    },
    formatFechaCorta(d) {
      return d ? new Date(d).toLocaleDateString('es-HN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
    },
    formatSize(bytes) {
      return bytes < 1024 * 1024 ? `${(bytes / 1024).toFixed(0)} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    },
  },
};
</script>

<style scoped>
.mi-cuenta {
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 0 60px;
  color: #1a1a1a;
}
.mi-cuenta.dark { color: #e5e5e5; }

/* ── Encabezado ── */
.mc-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 28px;
  padding-bottom: 20px;
  border-bottom: 1px solid rgba(0,0,0,0.08);
}
.dark .mc-header { border-bottom-color: rgba(255,255,255,0.07); }
.mc-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: inherit;
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 4px;
}
.mc-title i { color: #c09d62; }
.mc-sub { font-size: 0.8rem; color: #888; margin: 0; }

/* ── Stats ── */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 32px;
}
@media (max-width: 900px) { .stats-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 520px) { .stats-grid { grid-template-columns: 1fr; } }

.stat-card {
  background: #fff;
  border: 1px solid #eee;
  border-radius: 14px;
  padding: 20px;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  transition: box-shadow 0.2s;
}
.dark .stat-card { background: #1e1e1e; border-color: rgba(255,255,255,0.07); }
.stat-card:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.06); }

.stat-icon {
  width: 40px; height: 40px;
  border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  font-size: 1.2rem;
  flex-shrink: 0;
  background: rgba(192,157,98,0.12);
  color: #c09d62;
}
.stat-card.vigente   .stat-icon { background: rgba(74,222,128,0.12); color: #16a34a; }
.stat-card.por_vencer .stat-icon { background: rgba(251,191,36,0.12); color: #ca8a04; }
.stat-card.vencida   .stat-icon { background: rgba(239,68,68,0.12);  color: #dc2626; }

.stat-body { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.stat-label {
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #999;
}
.stat-val { font-size: 1.05rem; font-weight: 700; color: inherit; }
.stat-val.dim { color: #bbb; font-size: 0.82rem; font-weight: 400; }
.stat-date { font-size: 0.72rem; color: #aaa; margin-top: 2px; display: flex; align-items: center; gap: 6px; }

/* ── Secciones ── */
.mc-section { margin-bottom: 36px; }
.section-title {
  font-size: 1rem;
  font-weight: 700;
  color: inherit;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}
.section-title i { color: #c09d62; }
.section-title.danger { color: #dc2626; }
.section-title.danger i { color: #dc2626; }

/* ── Upload card ── */
.upload-card {
  background: #fff;
  border: 1px solid #eee;
  border-radius: 14px;
  padding: 24px;
}
.dark .upload-card { background: #1e1e1e; border-color: rgba(255,255,255,0.07); }

.form-row { display: flex; gap: 16px; margin-bottom: 20px; flex-wrap: wrap; }
.field { display: flex; flex-direction: column; gap: 6px; flex: 1; min-width: 120px; }
.field label {
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #888;
}
.field input, .field select {
  padding: 10px 13px;
  border: 1px solid #ddd;
  border-radius: 9px;
  font-size: 0.88rem;
  background: #f9f9f9;
  color: inherit;
  font-family: inherit;
  transition: border-color 0.2s;
}
.dark .field input, .dark .field select {
  background: #161616; border-color: rgba(255,255,255,0.1); color: #e5e5e5;
}
.field input:focus, .field select:focus {
  outline: none; border-color: #c09d62; box-shadow: 0 0 0 3px rgba(192,157,98,0.12);
}

/* ── Drop zone ── */
.drop-zone {
  border: 2px dashed #ddd;
  border-radius: 12px;
  padding: 36px 20px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
  margin-bottom: 20px;
}
.dark .drop-zone { border-color: rgba(255,255,255,0.15); }
.drop-zone:hover, .drop-zone.active { border-color: #c09d62; background: rgba(192,157,98,0.04); }
.drop-zone.has-file { border-color: #16a34a; background: rgba(74,222,128,0.04); }

.drop-icon { font-size: 2.2rem; color: #c09d62; display: block; margin-bottom: 10px; }
.drop-icon.selected { color: #16a34a; }
.drop-text { font-size: 0.85rem; color: #555; margin: 0 0 6px; }
.dark .drop-text { color: #aaa; }
.drop-hint { font-size: 0.72rem; color: #bbb; margin: 0; }

/* ── Botón subir ── */
.btn-subir {
  display: flex; align-items: center; gap: 8px;
  padding: 12px 28px;
  background: #198754;
  border: none; border-radius: 10px;
  color: #fff; font-size: 0.88rem; font-weight: 700;
  font-family: inherit; cursor: pointer;
  transition: opacity 0.2s, transform 0.15s;
}
.btn-subir:hover:not(:disabled) { background: #146c43; transform: translateY(-1px); }
.btn-subir:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }

.upload-msg {
  display: flex; align-items: center; gap: 8px;
  margin-top: 14px; padding: 12px 16px;
  border-radius: 9px; font-size: 0.83rem; font-weight: 500;
}
.upload-msg.ok    { background: rgba(74,222,128,0.1); color: #16a34a; border: 1px solid rgba(74,222,128,0.25); }
.upload-msg.error { background: rgba(239,68,68,0.08); color: #dc2626; border: 1px solid rgba(239,68,68,0.2); }

/* ── Historial table ── */
.tabla-wrap { overflow-x: auto; border-radius: 12px; border: 1px solid #eee; }
.dark .tabla-wrap { border-color: rgba(255,255,255,0.07); }

.historial-table { width: 100%; border-collapse: collapse; }
.historial-table thead { background: #f5f5f5; }
.dark .historial-table thead { background: #161616; }
.historial-table th {
  padding: 12px 16px;
  text-align: left;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #888;
  white-space: nowrap;
}
.historial-table td {
  padding: 13px 16px;
  font-size: 0.83rem;
  border-top: 1px solid #f0f0f0;
  color: inherit;
}
.dark .historial-table td { border-top-color: rgba(255,255,255,0.05); }
.historial-table tbody tr:hover { background: rgba(0,0,0,0.02); }
.dark .historial-table tbody tr:hover { background: rgba(255,255,255,0.03); }

.td-monto { font-weight: 700; font-variant-numeric: tabular-nums; }
.td-periodo { font-weight: 600; }
.td-fecha { color: #888; font-size: 0.77rem; white-space: nowrap; }
.dim { color: #bbb; }

.td-sub { display: block; font-size: 0.72rem; color: #888; font-weight: 400; margin-top: 2px; }
.td-monto-inline { font-variant-numeric: tabular-nums; color: #c09d62; font-weight: 600; }

.tipo-badge {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 8px; border-radius: 20px;
  font-size: 0.65rem; font-weight: 700; white-space: nowrap;
}
.tipo-renovacion  { background: rgba(99,102,241,0.1); color: #818cf8; border: 1px solid rgba(99,102,241,0.2); }
.tipo-comprobante { background: rgba(192,157,98,0.1); color: #c09d62; border: 1px solid rgba(192,157,98,0.2); }

.link-comprobante {
  display: inline-flex; align-items: center; gap: 5px;
  color: #c09d62; text-decoration: none; font-weight: 600;
  font-size: 0.8rem;
}
.link-comprobante:hover { text-decoration: underline; }

.nota-text { font-size: 0.78rem; color: #888; display: flex; align-items: center; gap: 6px; }
.nota-text i { color: #c09d62; flex-shrink: 0; }

/* ── Badge estado pago ── */
.pago-badge {
  display: inline-flex; align-items: center;
  padding: 3px 9px; border-radius: 20px;
  font-size: 0.68rem; font-weight: 700; letter-spacing: 0.04em;
  text-transform: uppercase; white-space: nowrap;
}
.pago-badge.pendiente { background: rgba(251,191,36,0.12); color: #ca8a04; }
.pago-badge.aprobado  { background: rgba(74,222,128,0.12);  color: #16a34a; }
.pago-badge.rechazado { background: rgba(239,68,68,0.1);    color: #dc2626; }

/* ── Zona de peligro ── */
/* ── Exportar datos ── */
.export-zone {
  border: 1.5px solid rgba(192,157,98,0.25);
  border-radius: 14px;
  padding: 24px;
  background: rgba(192,157,98,0.04);
}
.section-title.export { color: #c09d62; }
.btn-exportar {
  display: flex; align-items: center; gap: 8px;
  padding: 11px 24px;
  background: transparent;
  border: 1.5px solid rgba(192,157,98,0.5);
  border-radius: 10px;
  color: #c09d62; font-size: 0.85rem; font-weight: 700;
  font-family: inherit; cursor: pointer;
  transition: background 0.2s, border-color 0.2s;
}
.btn-exportar:hover:not(:disabled) { background: rgba(192,157,98,0.1); border-color: #c09d62; }
.btn-exportar:disabled { opacity: 0.5; cursor: not-allowed; }
.cancel-msg.ok { color: #22c55e; }

.danger-zone {
  border: 1.5px solid rgba(239,68,68,0.2);
  border-radius: 14px;
  padding: 24px;
  background: rgba(239,68,68,0.03);
}
.danger-desc { font-size: 0.83rem; color: #888; margin: 0 0 20px; line-height: 1.6; }
.danger-desc strong { color: inherit; }

.btn-cancelar {
  display: flex; align-items: center; gap: 8px;
  padding: 11px 24px;
  background: transparent;
  border: 1.5px solid rgba(239,68,68,0.4);
  border-radius: 10px;
  color: #dc2626; font-size: 0.85rem; font-weight: 700;
  font-family: inherit; cursor: pointer;
  transition: background 0.2s, border-color 0.2s;
}
.btn-cancelar:hover:not(:disabled) { background: rgba(239,68,68,0.08); border-color: #dc2626; }
.btn-cancelar:disabled { opacity: 0.5; cursor: not-allowed; }

.cancel-msg {
  margin-top: 14px; font-size: 0.82rem; color: #888;
}

/* ── Loading ── */
.mc-loading {
  display: flex; align-items: center; gap: 12px;
  padding: 40px; color: #888; font-size: 0.85rem;
  justify-content: center;
}
.mc-loading.small { padding: 20px; }
.spinner {
  width: 24px; height: 24px;
  border: 3px solid rgba(192,157,98,0.2);
  border-top-color: #c09d62;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
.spinner.small { width: 18px; height: 18px; border-width: 2px; }
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Empty state ── */
.empty-state {
  display: flex; flex-direction: column; align-items: center;
  gap: 8px; padding: 40px; color: #bbb; font-size: 0.85rem;
}
.empty-state i { font-size: 2rem; }

/* ── Modal cancelación ── */
.modal-overlay {
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex; align-items: center; justify-content: center;
  z-index: 9000;
}
.modal-card {
  background: #fff;
  border-radius: 18px;
  padding: 32px;
  max-width: 400px; width: 90%;
  display: flex; flex-direction: column;
  align-items: center; gap: 14px; text-align: center;
}
.dark .modal-card { background: #1e1e1e; }
.modal-icon { font-size: 2.5rem; color: #dc2626; }
.modal-card h3 { font-size: 1.1rem; font-weight: 700; margin: 0; color: inherit; }
.modal-card p  { font-size: 0.83rem; color: #888; line-height: 1.6; margin: 0; }
.modal-card p strong { color: inherit; }
.modal-btns { display: flex; gap: 12px; margin-top: 8px; }
.btn-modal-cancel {
  padding: 10px 20px; border-radius: 9px; border: 1px solid #ddd;
  background: transparent; font-size: 0.85rem; font-weight: 600;
  color: #888; cursor: pointer; font-family: inherit;
}
.btn-modal-cancel:hover { background: #f5f5f5; }
.btn-modal-confirm {
  padding: 10px 20px; border-radius: 9px; border: none;
  background: #dc2626; color: #fff; font-size: 0.85rem;
  font-weight: 700; cursor: pointer; font-family: inherit;
  transition: opacity 0.2s;
}
.btn-modal-confirm:hover { opacity: 0.88; }

.fade-enter-active, .fade-leave-active { transition: opacity 0.2s; }
.fade-enter-from,  .fade-leave-to      { opacity: 0; }

/* ── Modal cambio de plan ── */
.modal-icon.cambio { color: #c09d62; }
.btn-modal-cambio {
  padding: 10px 20px; border-radius: 9px; border: none;
  background: #198754;
  color: #fff; font-size: 0.85rem; font-weight: 700;
  cursor: pointer; font-family: inherit;
  display: flex; align-items: center; gap: 6px;
  transition: opacity 0.2s;
}
.btn-modal-cambio:hover:not(:disabled) { background: #146c43; }
.btn-modal-cambio:disabled { opacity: 0.5; cursor: not-allowed; }

/* ── Banner solicitud activa (pequeño, encima de proforma hint) ── */
.mc-solicitud-banner {
  display: flex; align-items: flex-start; gap: 14px;
  background: rgba(192,157,98,0.07);
  border: 1px solid rgba(192,157,98,0.3);
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 20px;
}
.dark .mc-solicitud-banner { background: rgba(192,157,98,0.06); }
.solicitud-banner-ico { font-size: 1.3rem; color: #c09d62; flex-shrink: 0; margin-top: 2px; }
.solicitud-banner-body { display: flex; flex-direction: column; gap: 4px; flex: 1; }
.solicitud-banner-titulo { font-size: 0.82rem; font-weight: 700; color: #c09d62; text-transform: uppercase; letter-spacing: 0.06em; }
.solicitud-banner-desc { font-size: 0.83rem; color: #666; line-height: 1.5; }
.dark .solicitud-banner-desc { color: #aaa; }
.solicitud-banner-desc strong { color: inherit; }
.btn-cancelar-solicitud {
  display: inline-flex; align-items: center; gap: 6px; flex-shrink: 0;
  padding: 7px 14px; border-radius: 8px;
  border: 1.5px solid rgba(239,68,68,0.3);
  background: transparent; color: #dc2626;
  font-size: 0.78rem; font-weight: 600; font-family: inherit;
  cursor: pointer; transition: background 0.2s; white-space: nowrap;
}
.btn-cancelar-solicitud:hover:not(:disabled) { background: rgba(239,68,68,0.07); border-color: #dc2626; }
.btn-cancelar-solicitud:disabled { opacity: 0.5; cursor: not-allowed; }

/* ── Stat card clickable ── */
.stat-card-clickable {
  cursor: pointer;
  transition: box-shadow 0.2s, border-color 0.2s, transform 0.15s;
}
.stat-card-clickable:hover {
  border-color: #c09d62;
  box-shadow: 0 4px 20px rgba(192,157,98,0.15);
  transform: translateY(-2px);
}

/* ── Modal selector de planes ── */
.modal-planes-card {
  width: 680px !important;
  max-width: 96vw !important;
  max-height: 85vh;
  overflow-y: auto;
  text-align: left;
  gap: 0;
  padding: 0;
  border-radius: 18px;
}
.modal-planes-header {
  display: flex; align-items: flex-start; justify-content: space-between;
  padding: 22px 24px 16px;
  border-bottom: 1px solid rgba(0,0,0,0.07);
  gap: 12px;
}
.dark .modal-planes-header { border-bottom-color: rgba(255,255,255,0.07); }
.modal-planes-header h3 {
  font-size: 1.05rem; font-weight: 700; color: inherit; margin: 0 0 4px;
  display: flex; align-items: center; gap: 8px;
}
.modal-planes-header p { font-size: 0.78rem; color: #888; margin: 0; }
.modal-x {
  background: none; border: none; color: #aaa;
  cursor: pointer; font-size: 1rem; padding: 4px 6px;
  border-radius: 7px; flex-shrink: 0;
  transition: background 0.15s;
}
.modal-x:hover { background: rgba(0,0,0,0.06); color: #333; }
.dark .modal-x:hover { background: rgba(255,255,255,0.08); color: #eee; }

/* Solicitud activa dentro del modal */
.planes-solicitud-activa {
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  margin: 14px 24px 0;
  background: rgba(251,191,36,0.08);
  border: 1px solid rgba(251,191,36,0.25);
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 0.8rem; color: #ca8a04; line-height: 1.4;
}
.dark .planes-solicitud-activa { background: rgba(251,191,36,0.06); }
.planes-solicitud-activa i { flex-shrink: 0; }
.planes-solicitud-activa span { flex: 1; }
.planes-solicitud-activa strong { color: #a16207; }
.dark .planes-solicitud-activa strong { color: #fbbf24; }
.btn-cancel-sol {
  padding: 4px 12px; border-radius: 6px;
  border: 1px solid rgba(239,68,68,0.3);
  background: transparent; color: #dc2626;
  font-size: 0.75rem; font-weight: 600; font-family: inherit;
  cursor: pointer; white-space: nowrap; flex-shrink: 0;
}
.btn-cancel-sol:hover:not(:disabled) { background: rgba(239,68,68,0.08); }
.btn-cancel-sol:disabled { opacity: 0.5; cursor: not-allowed; }

.planes-loading {
  display: flex; align-items: center; gap: 10px;
  padding: 30px 24px; color: #888; font-size: 0.83rem;
}

/* Grid de planes dentro del modal */
.planes-modal-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: 12px;
  padding: 18px 24px;
}

.plan-modal-card {
  background: #f9f9f9;
  border: 1.5px solid #eee;
  border-radius: 13px;
  padding: 16px 14px;
  display: flex; flex-direction: column; gap: 7px;
  position: relative;
  transition: box-shadow 0.15s, border-color 0.15s;
}
.dark .plan-modal-card { background: #161616; border-color: rgba(255,255,255,0.07); }
.plan-modal-card:hover { box-shadow: 0 3px 12px rgba(0,0,0,0.08); }

.plan-modal-card.pmc-actual {
  border-color: #c09d62;
  background: rgba(192,157,98,0.05);
}
.dark .plan-modal-card.pmc-actual { background: rgba(192,157,98,0.08); }

.plan-modal-card.pmc-solicitado {
  border-color: #818cf8;
  background: rgba(99,102,241,0.04);
}
.dark .plan-modal-card.pmc-solicitado { background: rgba(99,102,241,0.07); }

.pmc-tag {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 0.62rem; font-weight: 700; padding: 2px 8px;
  border-radius: 20px; align-self: flex-start;
}
.pmc-tag-actual { background: rgba(192,157,98,0.15); color: #c09d62; border: 1px solid rgba(192,157,98,0.3); }
.pmc-tag-sol    { background: rgba(99,102,241,0.12); color: #818cf8; border: 1px solid rgba(99,102,241,0.25); }

.pmc-nombre { font-size: 0.9rem; font-weight: 700; color: inherit; }
.pmc-desc   { font-size: 0.72rem; color: #888; line-height: 1.4; }
.pmc-precio { display: flex; align-items: baseline; gap: 4px; margin-top: 2px; }
.pmc-usd    { font-size: 1.05rem; font-weight: 800; color: #c09d62; font-variant-numeric: tabular-nums; }
.pmc-per    { font-size: 0.68rem; color: #aaa; }
.pmc-gratis { font-size: 0.78rem; font-weight: 700; color: #16a34a; }

.btn-sel-plan {
  margin-top: auto;
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  padding: 8px 12px; border-radius: 8px;
  border: 1.5px solid rgba(192,157,98,0.4);
  background: transparent; color: #c09d62;
  font-size: 0.77rem; font-weight: 700; font-family: inherit;
  cursor: pointer; transition: background 0.15s, border-color 0.15s;
}
.btn-sel-plan:hover { background: rgba(192,157,98,0.1); border-color: #c09d62; }

.planes-nota-modal {
  font-size: 0.74rem; color: #aaa;
  display: flex; align-items: flex-start; gap: 6px;
  line-height: 1.5; margin: 0;
  padding: 0 24px 20px;
}
.planes-nota-modal i { color: #c09d62; flex-shrink: 0; margin-top: 2px; }
.planes-nota-modal strong { color: #888; }

/* ── Proforma hint ── */
.mc-proforma-hint {
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(212,175,55,.08);
  border: 1px dashed rgba(212,175,55,.35);
  border-radius: 10px;
  padding: 12px 18px;
  margin-bottom: 18px;
  font-size: .875rem;
  color: var(--text-secondary, #888);
  flex-wrap: wrap;
}
.mc-proforma-hint i { color: #d4af37; font-size: 1.1rem; }
.btn-proforma {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-left: auto;
  padding: 6px 16px;
  background: #d4af37;
  color: #1a1d2e;
  border-radius: 7px;
  font-size: .82rem;
  font-weight: 700;
  text-decoration: none;
  transition: background .2s, transform .15s;
}
.btn-proforma:hover { background: #b8941f; transform: translateY(-1px); }
</style>
